import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../redux/store";
import { Property } from "../../types/property";
import { monthlyRentPcm } from "../../util/propertyParsing";
import SafeImage from "../SafeImage/SafeImage";

function getPostcodePrefix(postcode: string): string {
  const trimmed = postcode.trim().toUpperCase();
  if (!trimmed) return "";
  const outward = trimmed.split(/\s+/)[0];
  return outward;
}

function getMainImage(property: Property): string {
  if (property.media?.length && property.media[0].media_url) {
    return property.media[0].media_url;
  }
  if (property.photos?.length && property.photos[0].media_url) {
    return property.photos[0].media_url;
  }
  return "https://via.placeholder.com/400x300";
}

function findSimilarProperties(
  all: Property[],
  current: Property,
  limit = 8
): Property[] {
  const prefix = getPostcodePrefix(current.postcode ?? "");
  if (!prefix) return [];

  const currentPcm = monthlyRentPcm(current.price, current.rent_frequency);
  const minPrice = currentPcm * 0.8;
  const maxPrice = currentPcm * 1.2;
  const currentBeds = current.bedrooms;

  return all
    .filter((p) => {
      if (p.id === current.id) return false;
      const pPrefix = getPostcodePrefix(p.postcode ?? "");
      if (pPrefix !== prefix) return false;

      const pcm = monthlyRentPcm(p.price, p.rent_frequency);
      if (pcm < minPrice || pcm > maxPrice) return false;

      if (currentBeds != null && p.bedrooms != null) {
        if (Math.abs(p.bedrooms - currentBeds) > 1) return false;
      }

      return true;
    })
    .slice(0, limit);
}

interface SimilarPropertiesProps {
  property: Property;
}

const SimilarProperties: React.FC<SimilarPropertiesProps> = ({ property }) => {
  const navigate = useNavigate();
  const { items: properties } = useSelector(
    (state: RootState) => state.properties
  );
  const [scrollIndex, setScrollIndex] = useState(0);

  const similar = useMemo(
    () => findSimilarProperties(properties, property),
    [properties, property]
  );

  if (similar.length === 0) return null;

  const visibleCount = 3;
  const maxIndex = Math.max(0, similar.length - visibleCount);
  const canPrev = scrollIndex > 0;
  const canNext = scrollIndex < maxIndex;

  const visible = similar.slice(scrollIndex, scrollIndex + visibleCount);

  return (
    <section className="max-w-[1600px] mx-auto px-[5%] py-16">
      <div className="flex items-end justify-between gap-4 mb-8 border-b-2 border-b-solid border-b-brand-yellow pb-4">
        <div>
          <span className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-1">
            You may also like
          </span>
          <h2 className="page-section-heading">
            Similar Properties
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setScrollIndex((i) => Math.max(0, i - 1))}
            disabled={!canPrev}
            className="w-10 h-10 border border-solid border-[rgba(0,0,0,0.12)] bg-white font-teko text-xl disabled:opacity-30 hover:bg-brand-yellow transition-colors"
            aria-label="Previous properties"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setScrollIndex((i) => Math.min(maxIndex, i + 1))}
            disabled={!canNext}
            className="w-10 h-10 border border-solid border-[rgba(0,0,0,0.12)] bg-white font-teko text-xl disabled:opacity-30 hover:bg-brand-yellow transition-colors"
            aria-label="Next properties"
          >
            ›
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {visible.map((p) => {
          const pcm = monthlyRentPcm(p.price, p.rent_frequency);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() =>
                navigate(`/services/rent/properties/${p.id}`)
              }
              className="text-left bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl group"
            >
              <div className="relative h-48 overflow-hidden">
                <SafeImage
                  src={getMainImage(p)}
                  alt={p.display_address}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <div className="font-teko text-2xl text-white">
                    £{pcm.toLocaleString()} PCM
                  </div>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-teko text-2xl text-text-obsidian mb-1 line-clamp-2">
                  {p.display_address}
                </h3>
                <p className="font-manrope text-xs uppercase tracking-wider text-text-slate">
                  {p.property_type}
                  {p.bedrooms != null ? ` • ${p.bedrooms} Bed` : ""}
                  {p.bathrooms != null ? ` • ${p.bathrooms} Bath` : ""}
                </p>
                {p.postcode ? (
                  <p className="font-manrope text-sm text-text-grey mt-2">
                    {p.postcode}
                  </p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default SimilarProperties;
